/**
 * Fantasy Console 0 - Cart Card Component
 *
 * Displays a cart in the library with thumbnail, info, likes, and actions.
 */

import { useState, useRef, useEffect } from 'react'
import type { CartWithStats } from '../../services/cartService'
import {
  CopyIcon,
  EditIcon,
  ForkIcon,
  HeartIcon,
  ImagePlaceholderIcon,
  MoreIcon,
  ShareIcon,
  TrashIcon,
} from '../shared/icons'
import styles from './CartCard.module.css'

interface CartCardProps {
  cart: CartWithStats
  isActive: boolean
  isOwner: boolean
  isSignedIn: boolean
  onLoad: () => void
  onDelete: () => void
  onDuplicate: () => void
  onEditDetails: () => void
  onLike: () => void
  onFork: () => void
  onShare: () => void
}

export function CartCard({
  cart,
  isActive,
  isOwner,
  isSignedIn,
  onLoad,
  onDelete,
  onDuplicate,
  onEditDetails,
  onLike,
  onFork,
  onShare,
}: CartCardProps) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return

    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    return (
      date.toLocaleDateString() +
      ' ' +
      date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    )
  }

  return (
    <div className={`${styles.card} ${isActive ? styles.active : ''}`}>
      <div className={styles.thumbnail} onClick={onLoad}>
        {cart.thumbnailUrl ? (
          <img
            src={cart.thumbnailUrl}
            alt={cart.metadata.title}
            className={styles.thumbnailImage}
          />
        ) : (
          <div className={styles.placeholderThumbnail}>
            <ImagePlaceholderIcon />
            <span>No Preview</span>
          </div>
        )}

        {isActive && <div className={styles.activeBadge}>Current</div>}
      </div>

      <div className={styles.info}>
        <h3 className={styles.title} title={cart.metadata.title}>
          {cart.metadata.title}
        </h3>

        {/* Show username (author) - prefer username from profile, fallback to metadata.author */}
        <p className={styles.author}>
          by {cart.username || cart.metadata.author || 'Unknown'}
        </p>

        {cart.metadata.description && (
          <p className={styles.description}>{cart.metadata.description}</p>
        )}

        {/* Like count and stats row */}
        <div className={styles.statsRow}>
          <span className={styles.likeCount}>
            <HeartIcon filled={cart.isLikedByCurrentUser} />
            {cart.likeCount}
          </span>
          <span className={styles.fileCount}>
            {cart.codeFiles.length} file{cart.codeFiles.length !== 1 ? 's' : ''}
          </span>
        </div>

        <p className={styles.date}>Updated: {formatDate(cart.updatedAt)}</p>
      </div>

      <div className={styles.actions}>
        {/* Like Button */}
        <button
          onClick={onLike}
          className={`${styles.likeButton} ${cart.isLikedByCurrentUser ? styles.liked : ''}`}
          disabled={!isSignedIn}
          title={
            isSignedIn
              ? cart.isLikedByCurrentUser
                ? 'Unlike'
                : 'Like'
              : 'Sign in to like'
          }
        >
          <HeartIcon filled={cart.isLikedByCurrentUser} />
        </button>

        {/* Fork Button */}
        {!isOwner && (
          <button
            onClick={onFork}
            className={styles.forkButton}
            disabled={!isSignedIn}
            title={isSignedIn ? 'Fork this cart' : 'Sign in to fork'}
          >
            <ForkIcon />
          </button>
        )}

        {/* Share Button */}
        <button
          onClick={onShare}
          className={styles.forkButton}
          title='Copy share link'
        >
          <ShareIcon />
        </button>

        <button onClick={onLoad} className={styles.loadButton}>
          {isActive ? 'Reload' : 'Load'}
        </button>

        {/* Menu only shown for cart owners */}
        {isOwner && (
          <div className={styles.menuContainer} ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className={styles.menuButton}
              aria-label='More actions'
            >
              <MoreIcon />
            </button>

            {showMenu && (
              <div className={styles.menu}>
                <button
                  onClick={() => {
                    onEditDetails()
                    setShowMenu(false)
                  }}
                >
                  <EditIcon />
                  Edit Details
                </button>
                <button
                  onClick={() => {
                    onDuplicate()
                    setShowMenu(false)
                  }}
                >
                  <CopyIcon />
                  Duplicate
                </button>
                <button
                  onClick={() => {
                    onDelete()
                    setShowMenu(false)
                  }}
                  className={styles.dangerItem}
                >
                  <TrashIcon />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
